import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Leaf, Sun, Wind, Heart, Star, Film, Trash2 } from "lucide-react";
import Layout from "../components/Layout";
import PricingCards from "../components/PricingCards";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import { EText, ELink, EImg, ImageControls } from "../components/cms/Editable";
import { UploadButton } from "../components/cms/UploadButton";
import { mediaUrl } from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1613490537806-53663bfd0da8?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";
const EBOOK = "/api/media/a23939c6-b8c4-4e26-a302-e45ac5ec4b75";

const PILLARS = [
  { icon: Leaf, title: "Whole-Foods Healing", text: "Macrobiotic nutrition, East-West herbs, and time-tested home remedies." },
  { icon: Wind, title: "Breath & Movement", text: "Tai Chi Kung, breathwork, and yoga to circulate your vital life energy." },
  { icon: Sun, title: "Five Elements Wisdom", text: "Align your lifestyle with the seasons through Yin/Yang and the Five Elements." },
  { icon: Heart, title: "Spirit & Reflection", text: "Guided reflections and emotional mastery to shape your destiny from within." },
];

function HeroBackground() {
  const { get } = useSite();
  const videoUrl = get("hero_video_url", "");
  const vimeoId = get("hero_video_id", "570059883");
  const heroImg = get("hero_image", HERO);
  return (
    <div className="absolute inset-0 bg-brand-ink">
      {heroImg ? <img src={mediaUrl(heroImg)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : null}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-testid="hero-video">
        {videoUrl ? (
          <video src={mediaUrl(videoUrl)} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" data-testid="hero-video-file" />
        ) : vimeoId ? (
          <iframe
            src={`https://player.vimeo.com/video/${vimeoId}?badge=0&autopause=0&autoplay=1&muted=1&loop=1&background=1&title=0&byline=0&portrait=0`}
            title="Hero video" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ width: "100vw", height: "56.25vw", minHeight: "100%", minWidth: "177.78vh", border: 0 }}
            allow="autoplay; fullscreen; picture-in-picture"
          />
        ) : null}
      </div>
      <div className="absolute inset-0 bg-brand-ink/55" />
    </div>
  );
}

function HeroMediaControls() {
  const { editMode, save, reset } = useSite();
  if (!editMode) return null;
  const setVimeo = () => {
    const v = window.prompt("Vimeo video ID for the background (numbers only). Leave empty for no Vimeo video.");
    if (v !== null) { save("hero_video_id", v.trim()); save("hero_video_url", ""); }
  };
  return (
    <div className="absolute top-4 right-4 left-4 sm:left-auto z-20 flex flex-wrap gap-2 justify-end max-w-md" data-testid="hero-media-controls">
      <ImageControls k="hero_image" save={save} reset={reset} label="Background image" className="" />
      <UploadButton accept="video/mp4,video/webm,video/quicktime" isPublic label="Upload background video" testid="upload-hero-video"
        onUploaded={(m) => save("hero_video_url", m.url)} />
      <button type="button" onClick={setVimeo} className="cms-chip !static" data-testid="hero-vimeo-id"><Film size={13} /> Vimeo ID</button>
      <button type="button" onClick={() => { save("hero_video_url", ""); save("hero_video_id", ""); }} className="cms-chip !static" title="Remove video (image only)"><Trash2 size={13} /></button>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();

  return (
    <Layout>
      <section className="relative overflow-hidden">
        <HeroBackground />
        <HeroMediaControls />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8 py-28 lg:py-40 text-white">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <EText k="hero_badge" className="pill bg-white/15 text-white backdrop-blur">Isoharts · Zenergize Holistic Health Club</EText>
            <EText k="hero_title" as="h1" className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold mt-4 max-w-3xl leading-tight block" >
              Heal Yourself. Align Your Energy. Live Your Dharma.
            </EText>
            <EText k="hero_subtitle" as="p" multiline className="mt-5 max-w-xl text-white/85 text-base leading-relaxed block">
              Ancient wisdom — energy healing, macrobiotics, Tai Chi Kung, and Chinese Astrology — transformed into modern guided practices for your daily life.
            </EText>
            <div className="mt-8 flex flex-wrap gap-4">
              <ELink k="hero_cta_primary" to="/packages" className="btn-gold" testid="hero-join-club">Join The Club</ELink>
              <ELink k="hero_cta_secondary" to="/packages" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-brand-ink" testid="hero-explore-levels">Explore Membership Levels</ELink>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-pad bg-brand-cream" id="membership">
        <div className="text-center mb-12">
          <EText k="home.pricing.pill" className="pill bg-brand-cream2 text-brand-tan">Membership Packages</EText>
          <EText k="pricing_heading" as="h2" className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-ink mt-3 block">Choose Your Path to Vitality</EText>
          <EText k="pricing_subtext" as="p" multiline className="mt-3 text-brand-inksoft max-w-2xl mx-auto block">Cumulative, lifetime access. Start free and unlock more whenever you're ready — no punch passes, no subscriptions.</EText>
        </div>
        <div className="grid xl:grid-cols-[200px_minmax(0,1fr)_200px] gap-6 items-stretch max-w-[1500px] mx-auto">
          <EImg k="packages.side.left" src="/api/media/1ad58f8c-12d3-43c0-998a-31835ee70804" alt="Five Elements cycle"
            className="hidden xl:block w-full h-full min-h-[380px] object-cover rounded-2xl" wrapperClassName="relative h-full" />
          <PricingCards />
          <EImg k="packages.side.right" src="/api/media/fae76f84-9a1b-427b-bc62-710be86a425e" alt="One with Earth meditation"
            className="hidden xl:block w-full h-full min-h-[380px] object-cover rounded-2xl" wrapperClassName="relative h-full" />
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="mx-auto max-w-5xl grid md:grid-cols-2 gap-12 items-center">
          <div>
            <EText k="regenerate_heading" as="h2" className="font-display text-3xl sm:text-4xl font-semibold text-brand-ink block">Reawaken Your Body's Natural Ability to Heal</EText>
            <EText k="regenerate_body" as="p" multiline className="mt-4 text-brand-inksoft leading-relaxed block">
              Your body regenerates almost entirely every 120 days. New cells, new tissue, a new you — built from the food you eat, the breath you take, and the energy you cultivate.
            </EText>
            <EText k="home.regenerate.body2" as="p" multiline className="mt-3 text-brand-inksoft leading-relaxed block">
              Zenergize gives you the practices and guidance to make those 120 days count, so you rebuild yourself in harmony with nature's rhythms.
            </EText>
            <ELink k="home.regenerate.cta" to={user ? "/dashboard?tab=consult" : "/signup"} className="btn-primary mt-6" testid="book-consultation">Book A Consultation</ELink>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {PILLARS.map((p, i) => (
              <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="card-surface p-5">
                <p.icon className="text-brand-sage" />
                <EText k={`home.pillar.${i}.title`} as="h4" className="font-semibold text-brand-ink mt-3 text-sm block">{p.title}</EText>
                <EText k={`home.pillar.${i}.text`} as="p" className="text-xs text-brand-muted mt-1 leading-relaxed block">{p.text}</EText>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-brand-cream2">
        <div className="mx-auto max-w-5xl card-surface p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <EText k="home.astro.pill" className="pill bg-white text-brand-tan">Free · Instant</EText>
            <EText k="home.astro.title" as="h2" className="font-display text-3xl sm:text-4xl font-semibold text-brand-ink mt-3 block">Get Your Free Chinese Astrology Reading</EText>
            <EText k="home.astro.text" as="p" multiline className="mt-3 text-brand-inksoft block">Instantly calculated from your birth date — discover your animal, element, and personal energetic path.</EText>
            <ELink k="home.astro.cta" to="/chinese-astrology" className="btn-primary mt-6" testid="home-astrology-cta">Calculate My Reading</ELink>
          </div>
          <div className="flex justify-center gap-3 text-brand-sage">
            {["Wood", "Fire", "Earth", "Metal", "Water"].map((el) => (
              <div key={el} className="text-center">
                <div className="w-12 h-12 rounded-full bg-white border border-brand-line flex items-center justify-center font-display text-lg">{el[0]}</div>
                <span className="text-[10px] text-brand-muted mt-1 block">{el}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="mx-auto max-w-5xl grid md:grid-cols-2 gap-10 items-center">
          <EImg k="home.ebook.image" src={EBOOK} alt="Shaping Our Destiny book cover" className="rounded-2xl w-full object-contain aspect-[4/3] bg-black" />
          <div>
            <Sparkles className="text-brand-gold" />
            <EText k="home.ebook.title" as="h2" className="font-display text-3xl sm:text-4xl font-semibold text-brand-ink mt-2 block">Shaping Our Destiny</EText>
            <EText k="home.ebook.text" as="p" multiline className="mt-3 text-brand-inksoft leading-relaxed block">Body reading & lifestyle recommendations for health, love & life path — Seymour Koblin's complete guide.</EText>
            <ELink k="home.ebook.cta" to="/shop?product=B01" className="btn-gold mt-6" testid="buy-book">Buy This Book</ELink>
          </div>
        </div>
      </section>

    </Layout>
  );
}
